import * as React from "react";
import styled from "@emotion/styled";
import { Table as TableV5 } from "antdv5";
import type { TablePaginationConfig } from "antdv5";
import type { IGameDetail, IGameTop } from "../../../api_types";
import type { SetState, Fn, ITableData } from "../../../client_types";
import { context } from "components/context/Context";
import { computePrivilege } from "../../../util";

interface IFindName {
  matchGameID: string;
  searchName: string;
  searchFeetNo: string;
}
interface IPageInfo {
  pageSize: number;
  current: number;
}
interface IProps {
  type: string;
  isMatch: boolean;
  grade: ITableData<IGameDetail>;
  gather: ITableData<IGameDetail>;
  loading: boolean;
  data: IGameTop[];
  findName: IFindName;
  pageInfo: {
    pageSize: number;
    current: number;
  };
  settings: Record<string, any>;
  setFindName: SetState<IFindName>;
  setPageInfo: SetState<IPageInfo>;
  setLoading: SetState<boolean>;
  setCountDown: SetState<number>;
  handleCurrentPage: () => number;
  onTableChange: (pagination: TablePaginationConfig) => void;
  searchGather: Fn;
  searchGrade: Fn;
}

const StyledAntdTable = styled(TableV5)`
  margin-top: 0.5rem;
`;

const computeIndex = (i: number, size: number, page: number) => {
  return (page - 1) * size + (i + 1);
};

const RaceliveTable = ({
  type,
  grade,
  gather,
  loading,
  data,
  findName,
  isMatch,
  pageInfo,
  settings,
  setFindName,
  setPageInfo,
  setLoading,
  setCountDown,
  onTableChange,
  handleCurrentPage,
  searchGrade,
  searchGather,
}: IProps) => {
  const StringE_HMS = data[0]?.StringE_HMS || "0°0′0″";
  const StringN_HMS = data[0]?.StringN_HMS || "0°0′0″";
  const hasCoordinates = StringE_HMS !== "0°0′0″" && StringN_HMS !== "0°0′0″";
  const { state, dispatch } = React.useContext(context);

  const columns = [
    {
      title: "序",
      dataIndex: "PageRank",
      onCell: () => {
        return {
          style: {
            width: "60px",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          } as React.CSSProperties,
        };
      },
      render: (text: string, record: any, index: number) => (
        <td>{computeIndex(index, pageInfo.pageSize, pageInfo.current)}</td>
      ),
    },
    {
      title: "单关暂排",
      dataIndex: "Rank",
      onCell: () => ({
        style: {
          width: "85px",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          overflow: "hidden",
        } as React.CSSProperties,
      }),
      hidden: !computePrivilege(
        state.character,
        Number.parseInt(settings?.SHOWRANK || "1")
      ),
    },
    {
      title: "单关分速",
      dataIndex: "MetresPerMin",
      render: (text: string) => (
        <div className="">{Number(text).toFixed(4)}</div>
      ),
      onCell: () => {
        return {
          style: {
            width: "100px",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          } as React.CSSProperties,
        };
      },
      sortter: {
        compare: (a: number, b: number) => a - b,
        multiple: 2,
      },
      hidden: !computePrivilege(
        state.character,
        Number.parseInt(settings?.SHOWRANK || "1")
      ),
    },
    {
      title: "平均暂排",
      dataIndex: "AvgRank",
      render: (text: string) => <div className="">{text}</div>,
      onCell: () => {
        return {
          style: {
            width: "95px",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          } as React.CSSProperties,
        };
      },
      hidden: !computePrivilege(
        state.character,
        Number.parseInt(settings?.SHOWAVGRANK || "1")
      ),
    },
    {
      title: "平均分速",
      dataIndex: "AvgMetresPerMin",
      render: (text: string) => <div className="">{text}</div>,
      onCell: () => {
        return {
          style: {
            width: "115px",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          } as React.CSSProperties,
        };
      },
      hidden: !computePrivilege(
        state.character,
        Number.parseInt(settings?.SHOWAVGRANK || "1")
      ),
    },
    {
      title: "综合排名",
      dataIndex: "TotalRank",
      render: (text: string) => <div className="">{text}</div>,
      onCell: () => {
        return {
          style: {
            maxWidth: 100,
            minWidth: 100,
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          } as React.CSSProperties,
        };
      },
      hidden: true,
    },
    {
      title: "参赛名",
      dataIndex: "LoftName",
      render: (text: string) => (
        <div
          style={{ color: "#3d6bff", cursor: "pointer" }}
          onClick={() => {
            setFindName({ ...findName, searchName: text.trim() });
            setPageInfo({ pageSize: 100, current: 1 });
            searchGrade();
            setLoading(true);
            setCountDown(60);
          }}
        >
          {text}
        </div>
      ),
      onCell: () => {
        return {
          style: {
            maxWidth: "25%",
            minWidth: "300px",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          } as React.CSSProperties,
        };
      },
    },
    {
      title: "区码",
      dataIndex: "LoftAreaName",
      onCell: () => {
        return {
          style: {
            minWidth: 80,
            // whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          } as React.CSSProperties,
        };
      },
    },
    {
      title: "环号",
      dataIndex: "FeetNo",
      onCell: () => {
        return {
          style: {
            maxWidth: "12%",
            minWidth: "250px",
            // whiteSpace: "wrap",
            // what is this???? "wrap" isn't a valid value to white-space
            textOverflow: "ellipsis",
            overflow: "hidden",
          } as React.CSSProperties,
        };
      },
    },
    {
      title: "羽色",
      dataIndex: "Feather",
      onCell: () => {
        return {
          style: {
            maxWidth: "12%",
            minWidth: "150px",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          } as React.CSSProperties,
        };
      },
    },
    {
      title: "综合分速(米/分)",
      dataIndex: "TotalMetresPerMin",
      render: (text: string) => (
        <div className="">{Number(text).toFixed(4)}</div>
      ),
      onCell: () => {
        return {
          style: {
            maxWidth: 100,
            minWidth: 100,
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          } as React.CSSProperties,
        };
      },
      defaultSortOrder: "descend" as "descend" | "ascend" | null,
      hidden: true,
    },
    {
      title: "平均分速(米/分)",
      dataIndex: "AvgMetresPerMin",
      render: (text: string) => (
        <div className="">{Number(text).toFixed(4)}</div>
      ),
      onCell: () => {
        return {
          style: {
            maxWidth: 100,
            minWidth: 100,
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          } as React.CSSProperties,
        };
      },
      hidden: true,
    },
    {
      title: "归巢时间",
      dataIndex: "StringDate",
      onCell: () => {
        return {
          style: {
            width: "250px",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
            fontVariantNumeric: "tabular-nums",
          } as React.CSSProperties,
        };
      },
    },
    {
      title: "空距(米)",
      dataIndex: "Distance",
      onCell: () => {
        return {
          style: {
            width: "85px",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          } as React.CSSProperties,
        };
      },
      hidden: !isMatch && !hasCoordinates,
    },
  ].filter((item) => !item.hidden);

  const columnsGather = [
    {
      title: "序号",
      dataIndex: "rank",
      width: 10,
      onCell: () => {
        return {
          style: {
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          } as React.CSSProperties,
        };
      },
    },
    {
      title: "参赛名",
      dataIndex: "LoftName",
      width: 200,
      onCell: () => {
        return {
          style: {
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          } as React.CSSProperties,
        };
      },
      render: (text: string) => (
        <div
          style={{
            color: "#3d6bff",
            cursor: "pointer",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          onClick={() => {
            setFindName({ ...findName, searchName: text.trim() });
            setPageInfo({ pageSize: 100, current: 1 });
            searchGather();
            setLoading(true);
            setCountDown(60);
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title: "环号",
      dataIndex: "FeetNo",
      width: 10,
      onCell: () => {
        return {
          style: {
            // whiteSpace: "wrap",
            // same as above
            textOverflow: "ellipsis",
            overflow: "hidden",
          } as React.CSSProperties,
        };
      },
    },
    {
      title: "羽色",
      dataIndex: "Feather",
      width: 10,
      onCell: () => {
        return {
          style: {
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          } as React.CSSProperties,
        };
      },
    },
    {
      title: "点名时间",
      dataIndex: "StringDate",
      width: 100,
      onCell: () => {
        return {
          style: {
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          } as React.CSSProperties,
        };
      },
      hidden: true,
    },
  ].filter((d) => !d.hidden);

  return type === "grade" ? (
    <StyledAntdTable
      className="bordered-table"
      columns={columns}
      dataSource={grade.data}
      pagination={{
        pageSizeOptions: ["20", "50", "100"],
        locale: { items_per_page: "笔/页" },
        defaultPageSize: 100,
        total: parseInt(grade.count) || 0,
        showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} 笔`,
        showQuickJumper: true,
        current: handleCurrentPage(),
        position: ["topRight", "bottomRight"],
        responsive: true,
      }}
      onChange={onTableChange as (pagination: TablePaginationConfig) => void}
      scroll={{ x: "max-content" }}
      rowKey="FeetNo"
      loading={loading}
      size="small"
      bordered
    />
  ) : type === "gather" ? (
    <StyledAntdTable
      className="bordered-table"
      columns={columnsGather}
      dataSource={gather.data}
      pagination={{
        pageSizeOptions: ["20", "50", "100"],
        locale: { items_per_page: "笔/页" },
        defaultPageSize: 100,
        total: gather.totalPage,
        showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} 笔`,
        showQuickJumper: true,
        current: handleCurrentPage(),
        position: ["topRight", "bottomRight"],
        responsive: true,
        hideOnSinglePage: false,
      }}
      onChange={onTableChange as (pagination: TablePaginationConfig) => void}
      scroll={{ x: "max-content" }}
      rowKey="FeetNo"
      loading={loading}
      size="small"
      bordered
    />
  ) : (
    <StyledAntdTable
      className="bordered-table"
      columns={columnsGather}
      dataSource={[]}
      pagination={{
        pageSizeOptions: ["20", "50", "100"],
        locale: { items_per_page: "笔/页" },
        defaultPageSize: 100,
        total: 0,
        showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} 笔`,
        showQuickJumper: true,
        responsive: true,
        hideOnSinglePage: false,
      }}
      onChange={onTableChange as (pagination: TablePaginationConfig) => void}
      scroll={{ x: "max-content" }}
      rowKey="FeetNo"
      size="small"
      bordered
      loading={loading}
    />
  );
};

export default RaceliveTable;
